/**
 * Shared German label maps for the web UI.
 *
 * 1:1 port of `/app/mobile/src/utils/germanLabels.ts` — keep both in
 * sync. Backend stores raw enum values (e.g. `semi_detached_house`,
 * `petrol`, `manual`); the UI pipes them through these maps before
 * rendering. The raw key stays untouched when sent back via the
 * preferences PUT — only the display layer is localised.
 *
 * Web doesn't currently surface body_type or feature labels (those
 * are mobile-only swipe-card surfaces) so this file is a focused
 * subset of the mobile version covering exactly what web pages need.
 */

export const OBJECT_TYPE_DE = {
  apartment: "Wohnung",
  ground_floor_apartment: "Erdgeschoss-Wohnung",
  penthouse: "Penthouse",
  maisonette: "Maisonette",
  loft: "Loft",
  house: "Haus",
  villa: "Villa",
  detached_house: "Einfamilienhaus",
  semi_detached_house: "Doppelhaushälfte",
  row_house: "Reihenhaus",
  land: "Grundstück",
  studio: "Studio",
  unknown: "Immobilie",
};

export const TRANSACTION_TYPE_DE = {
  buy: "Kauf",
  rent: "Miete",
};

// 1:1 with mobile germanLabels.ts — web ListingDetailPage now surfaces
// fuel / gearbox / body_type too.
export const FUEL_DE = {
  petrol: "Benzin",
  benzin: "Benzin",
  diesel: "Diesel",
  electric: "Elektro",
  elektro: "Elektro",
  hybrid: "Hybrid",
  hybrid_petrol: "Hybrid (Benzin)",
  hybrid_diesel: "Hybrid (Diesel)",
  plugin_hybrid: "Plug-in Hybrid",
  gas: "Gas",
  lpg: "LPG",
  cng: "CNG (Erdgas)",
  hydrogen: "Wasserstoff",
  unknown: "Kraftstoff unbekannt",
};

export const GEARBOX_DE = {
  manual: "Schaltgetriebe",
  schaltgetriebe: "Schaltgetriebe",
  automatic: "Automatik",
  automatik: "Automatik",
  semi_automatic: "Halbautomatik",
  unknown: "Getriebe unbekannt",
};

export const BODY_TYPE_DE = {
  sedan: "Limousine",
  wagon: "Kombi",
  station_wagon: "Kombi",
  suv: "SUV",
  hatchback: "Kompakt",
  coupe: "Coupé",
  convertible: "Cabrio",
  van: "Van",
  minivan: "Minivan",
  pickup: "Pickup",
  small_car: "Kleinwagen",
  city_car: "Kleinstwagen",
  sports_car: "Sportwagen",
  microcar: "Mopedauto",
};

// Risk flags come from the backend's `mapping_warnings` — mostly
// internal parser notes. Only the user-relevant ones are whitelisted
// here; everything else is hidden (never render a raw slug).
// MUST stay in lock-step with `/app/mobile/src/utils/germanLabels.ts`.
const RISK_FLAG_WHITELIST = ["not_accident_free", "price_on_request"];

export function riskFlagLabels(flags) {
  // Welle 3 — labels live in the `listing` namespace (risk.*).
  // eslint-disable-next-line global-require
  const i18n = require("../i18n").default;
  return (flags || [])
    .map((f) => String(f).toLowerCase().trim())
    .filter((f) => RISK_FLAG_WHITELIST.includes(f))
    .map((f) => i18n.t(`listing:risk.${f}`));
}

export function localizeOrFallback(map, value) {
  if (!value) return "";
  const key = String(value).toLowerCase().trim();
  return map[key] ?? value;
}

/**
 * Curated brand list for the Marken multi-select. Chip label is what
 * the user sees; value is what the backend stores (matches the
 * canonical `brand_canonical` slugs that the car-master CSV produces).
 *
 * Order: by DB-listings count descending (snapshot 2026-06-13). The
 * top entries are the most-frequently-listed brands, so users see
 * what's likely available first.
 *
 * MUST stay in lock-step with the mobile constant of the same name in
 * `/app/mobile/src/utils/germanLabels.ts` AND the backend slug map in
 * `/app/backend/app/services/feed/make_normalization.py`
 * (`MAKE_SLUG_TO_CANONICAL`). 54 entries — the DB catch-all bucket
 * "Sonstige" is intentionally excluded from the chip list (still
 * accepted server-side if ever sent).
 *
 * Slug convention: lowercase + hyphen for spaces (`alfa-romeo`,
 * `aston-martin`, `land-rover`). The legacy slug `"land rover"` (with
 * a literal space) is also accepted server-side for back-compat.
 */
export const COMMON_CAR_BRANDS = [
  { value: "volkswagen", label: "Volkswagen" },
  { value: "audi", label: "Audi" },
  { value: "mercedes-benz", label: "Mercedes-Benz" },
  { value: "bmw", label: "BMW" },
  { value: "mazda", label: "Mazda" },
  { value: "renault", label: "Renault" },
  { value: "hyundai", label: "Hyundai" },
  { value: "kia", label: "Kia" },
  { value: "skoda", label: "Škoda" },
  { value: "peugeot", label: "Peugeot" },
  { value: "ford", label: "Ford" },
  { value: "opel", label: "Opel" },
  { value: "fiat", label: "Fiat" },
  { value: "nissan", label: "Nissan" },
  { value: "dacia", label: "Dacia" },
  { value: "mg", label: "MG" },
  { value: "seat", label: "SEAT" },
  { value: "suzuki", label: "Suzuki" },
  { value: "citroen", label: "Citroën" },
  { value: "jeep", label: "Jeep" },
  { value: "alfa-romeo", label: "Alfa Romeo" },
  { value: "porsche", label: "Porsche" },
  { value: "volvo", label: "Volvo" },
  { value: "toyota", label: "Toyota" },
  { value: "mini", label: "MINI" },
  { value: "land-rover", label: "Land Rover" },
  { value: "jaguar", label: "Jaguar" },
  { value: "tesla", label: "Tesla" },
  { value: "lexus", label: "Lexus" },
  { value: "leapmotor", label: "Leapmotor" },
  { value: "cupra", label: "CUPRA" },
  { value: "smart", label: "Smart" },
  { value: "honda", label: "Honda" },
  { value: "mitsubishi", label: "Mitsubishi" },
  { value: "chevrolet", label: "Chevrolet" },
  { value: "abarth", label: "Abarth" },
  { value: "lamborghini", label: "Lamborghini" },
  { value: "maserati", label: "Maserati" },
  { value: "subaru", label: "Subaru" },
  { value: "ferrari", label: "Ferrari" },
  { value: "alpine", label: "Alpine" },
  { value: "pontiac", label: "Pontiac" },
  { value: "aixam", label: "Aixam" },
  { value: "aston-martin", label: "Aston Martin" },
  { value: "dodge", label: "Dodge" },
  { value: "morgan", label: "Morgan" },
  { value: "byd", label: "BYD" },
  { value: "ds", label: "DS" },
  { value: "polestar", label: "Polestar" },
  { value: "ineos", label: "Ineos" },
  { value: "buick", label: "Buick" },
  { value: "lancia", label: "Lancia" },
  { value: "mclaren", label: "McLaren" },
  { value: "ssangyong", label: "SsangYong" },
  { value: "ligier", label: "Ligier" },
];
